import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Copy,
  Check,
  Eye,
  RefreshCw,
  MessageCircle,
} from "lucide-react";
import { getDisplayName } from "../../utils/metalNameFixer";
import { formatTradeId } from "../../utils/tradeIdFormatter";
import API_BASE from "../../config/api";

const AdminPaymentsTab = ({ onStatsChange }) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterStatus, setFilterStatus] = useState("pending_verification");
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedUtr, setCopiedUtr] = useState("");

  // Modal states
  const [previewImage, setPreviewImage] = useState(null);
  const [declineModalTrade, setDeclineModalTrade] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Helper to reliably retrieve valid admin token
  const getAdminToken = () => {
    return localStorage.getItem("adminToken") || localStorage.getItem("token") || "";
  };

  const fetchTrades = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const token = getAdminToken();
      const res = await fetch(`${API_BASE}/api/trades/all`, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });

      if (!res.ok) throw new Error("Failed to load trades");
      const data = await res.json();
      const submitted = Array.isArray(data)
        ? data.filter((t) => t.status !== "pending_payment")
        : [];
      setTrades(submitted);

      if (onStatsChange) {
        const pendingCount = submitted.filter(
          (t) => t.status === "pending_verification"
        ).length;
        onStatsChange(pendingCount);
      }
    } catch (err) {
      console.error("Admin fetch payments error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTrades();
    // Auto-refresh every 7 seconds for live payments
    const timer = setInterval(() => {
      fetchTrades(true);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  // One-click copy
  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedUtr(id);
    setTimeout(() => setCopiedUtr(""), 2000);
  };

  // Confirm Payment & Approve Trade
  const handleConfirmTrade = async (trade) => {
    const isYes = window.confirm(
      `Approve payment of ₹${trade.totalPrice?.toLocaleString("en-IN")} and CONFIRM trade for ${trade.fullName}?`
    );
    if (!isYes) return;

    const token = getAdminToken();
    if (!token) {
      alert("Admin session expired or missing token. Please log in again.");
      window.location.href = "/admin-login";
      return;
    }

    try {
      setActionLoading(true);
      const res = await fetch(
        `${API_BASE}/api/trades/${trade._id}/verify`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: "confirmed",
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          alert("Admin session expired (Forbidden / Invalid token). Please log in to admin panel again.");
          localStorage.removeItem("adminToken");
          localStorage.removeItem("admin");
          window.location.href = "/admin-login";
          return;
        }
        throw new Error(data.message || "Failed to confirm trade");
      }

      // Update state locally
      setTrades((prev) =>
        prev.map((t) => (t._id === trade._id ? { ...t, status: "confirmed" } : t))
      );
    } catch (err) {
      alert("Error confirming trade: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Decline Trade
  const handleDeclineSubmit = async (e) => {
    e.preventDefault();
    if (!declineModalTrade) return;

    const token = getAdminToken();
    if (!token) {
      alert("Admin session expired or missing token. Please log in again.");
      window.location.href = "/admin-login";
      return;
    }

    try {
      setActionLoading(true);
      const res = await fetch(
        `${API_BASE}/api/trades/${declineModalTrade._id}/verify`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: "declined",
            declineReason: declineReason.trim() || "Payment not credited to account",
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          alert("Admin session expired (Forbidden / Invalid token). Please log in to admin panel again.");
          localStorage.removeItem("adminToken");
          localStorage.removeItem("admin");
          window.location.href = "/admin-login";
          return;
        }
        throw new Error(data.message || "Failed to decline trade");
      }

      setTrades((prev) =>
        prev.map((t) =>
          t._id === declineModalTrade._id
            ? { ...t, status: "declined", declineReason }
            : t
        )
      );
      setDeclineModalTrade(null);
      setDeclineReason("");
    } catch (err) {
      alert("Error declining trade: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Calculations for stats
  const stats = useMemo(() => {
    let pending = 0;
    let confirmed = 0;
    let declined = 0;
    let totalVerifiedAmount = 0;

    trades.forEach((t) => {
      if (t.status === "pending_verification") pending++;
      else if (t.status === "confirmed") {
        confirmed++;
        totalVerifiedAmount += Number(t.totalPrice || 0);
      } else if (t.status === "declined") declined++;
    });

    return { pending, confirmed, declined, totalVerifiedAmount };
  }, [trades]);

  // Filtered trades
  const filteredTrades = useMemo(() => {
    return trades.filter((t) => {
      // Status filter
      if (filterStatus === "pending_verification") {
        if (t.status !== "pending_verification") return false;
      } else if (filterStatus === "confirmed") {
        if (t.status !== "confirmed") return false;
      } else if (filterStatus === "declined") {
        if (t.status !== "declined") return false;
      } else if (filterStatus === "pending_payment") {
        if (t.status !== "pending_payment") return false;
      }

      // Search filter
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const tradeIdMatch = formatTradeId(t).toLowerCase().includes(query);
        const nameMatch = (t.fullName || "").toLowerCase().includes(query);
        const phoneMatch = (t.phone || "").toLowerCase().includes(query);
        const metalMatch = (t.metal || "").toLowerCase().includes(query);
        const utrMatch = (t.utr || "").toLowerCase().includes(query);
        return tradeIdMatch || nameMatch || phoneMatch || metalMatch || utrMatch;
      }

      return true;
    });
  }, [trades, filterStatus, searchTerm]);

  return (
    <Wrapper>
      {/* 📊 KPI Summary Stats */}
      <StatsRow>
        <StatCard className="alert">
          <div className="icon-wrap">
            <Clock size={20} />
          </div>
          <div className="info">
            <span className="count">{stats.pending}</span>
            <span className="label">Pending Verifications</span>
          </div>
        </StatCard>

        <StatCard className="success">
          <div className="icon-wrap">
            <CheckCircle size={20} />
          </div>
          <div className="info">
            <span className="count">{stats.confirmed}</span>
            <span className="label">Confirmed Payments</span>
          </div>
        </StatCard>

        <StatCard className="danger">
          <div className="icon-wrap">
            <XCircle size={20} />
          </div>
          <div className="info">
            <span className="count">{stats.declined}</span>
            <span className="label">Declined / Failed</span>
          </div>
        </StatCard>

        <StatCard>
          <div className="icon-wrap">
            <CreditCard size={20} />
          </div>
          <div className="info">
            <span className="count">₹ {stats.totalVerifiedAmount.toLocaleString("en-IN")}</span>
            <span className="label">Total Verified Funds</span>
          </div>
        </StatCard>
      </StatsRow>

      {/* 🔍 Search & Filters Bar */}
      <FilterBar>
        <FilterTabs>
          <FilterTabBtn
            $active={filterStatus === "pending_verification"}
            onClick={() => setFilterStatus("pending_verification")}
            className="pending-tab"
          >
            Pending Verifications ({stats.pending})
          </FilterTabBtn>
          <FilterTabBtn
            $active={filterStatus === "confirmed"}
            onClick={() => setFilterStatus("confirmed")}
          >
            Confirmed ({stats.confirmed})
          </FilterTabBtn>
          <FilterTabBtn
            $active={filterStatus === "declined"}
            onClick={() => setFilterStatus("declined")}
          >
            Declined ({stats.declined})
          </FilterTabBtn>
          <FilterTabBtn
            $active={filterStatus === "all"}
            onClick={() => setFilterStatus("all")}
          >
            All Trades ({trades.length})
          </FilterTabBtn>
        </FilterTabs>

        <SearchAndRefresh>
          <SearchWrap>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search user, phone, UTR..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchWrap>
          <RefreshBtn onClick={() => fetchTrades(false)} title="Refresh data">
            <RefreshCw size={15} className={refreshing ? "spinner" : ""} />
            <span>Refresh</span>
          </RefreshBtn>
        </SearchAndRefresh>
      </FilterBar>

      {/* 📋 Payments List / Table */}
      {loading ? (
        <LoadingWrap>
          <RefreshCw className="spinner" size={32} color="#f5d76e" />
          <p>Loading payments desk...</p>
        </LoadingWrap>
      ) : filteredTrades.length === 0 ? (
        <EmptyWrap>
          <CreditCard size={44} color="#64748b" />
          <h3>No Payment Records Found</h3>
          <p>
            {filterStatus === "pending_verification"
              ? "All pending payments have been verified! New incoming payments will appear here automatically."
              : "No records match the current filter criteria."}
          </p>
        </EmptyWrap>
      ) : (
        <PaymentsGrid>
          {filteredTrades.map((t) => {
            const isPending = t.status === "pending_verification";
            const isConfirmed = t.status === "confirmed";
            const isDeclined = t.status === "declined";

            return (
              <PaymentCard key={t._id} $status={t.status}>
                {/* Trade ID Subhead */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                  <span style={{ color: "#f5d76e", fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.5px" }}>
                    Trade ID: {formatTradeId(t)}
                  </span>
                  <span style={{ color: "#64748b", fontSize: "0.74rem" }}>
                    {new Date(t.timestamp).toLocaleDateString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>

                {/* Header: User & Status Badge */}
                <CardHead>
                  <UserInfo>
                    <h4>{t.fullName || "Valued Trader"}</h4>
                    <span className="phone">
                      {t.phone}
                      <a
                        href={`https://wa.me/91${t.phone?.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wa-btn"
                        title="Chat on WhatsApp"
                      >
                        <MessageCircle size={13} />
                      </a>
                    </span>
                  </UserInfo>

                  <StatusPill $status={t.status}>
                    {isPending && (
                      <>
                        <Clock size={13} className="spinner" /> PENDING VERIFICATION
                      </>
                    )}
                    {isConfirmed && (
                      <>
                        <CheckCircle size={13} /> CONFIRMED
                      </>
                    )}
                    {isDeclined && (
                      <>
                        <XCircle size={13} /> DECLINED
                      </>
                    )}
                    {t.status === "pending_payment" && (
                      <>
                        <CreditCard size={13} /> AWAITING PROOF
                      </>
                    )}
                  </StatusPill>
                </CardHead>

                {/* Amount & Trade Details */}
                <TradeSummary>
                  <div className="amount-box">
                    <span className="title">Amount Payable:</span>
                    <span className="amount">
                      ₹ {Number(t.totalPrice || 0).toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="meta-info">
                    <span>
                      <strong>Metal:</strong> {getDisplayName(t.metal)}
                    </span>
                    <span>
                      <strong>Qty:</strong> {t.quantity}
                    </span>
                    <span>
                      <strong>Type:</strong>{" "}
                      <span className={`action-badge ${t.action}`}>
                        {t.action?.toUpperCase()}
                      </span>
                    </span>
                  </div>
                </TradeSummary>

                {/* Payment Proof Section: UTR & Screenshot */}
                <ProofSection>
                  <div className="utr-row">
                    <span className="lbl">Method:</span>
                    <span className="val">{t.paymentMethod || "UPI"}</span>
                  </div>

                  <div className="utr-row">
                    <span className="lbl">UTR / Ref:</span>
                    <span className="val mono">
                      {t.utr || <span className="dim">Not provided</span>}
                    </span>
                    {t.utr && (
                      <button
                        className="copy-btn"
                        onClick={() => handleCopy(t.utr, t._id)}
                        title="Copy UTR"
                      >
                        {copiedUtr === t._id ? <Check size={12} color="#10b981" /> : <Copy size={12} />}
                        {copiedUtr === t._id ? "Copied" : "Copy"}
                      </button>
                    )}
                  </div>

                  <div className="screenshot-row">
                    <span className="lbl">Payment Receipt:</span>
                    {t.paymentScreenshot ? (
                      <ReceiptPreview onClick={() => setPreviewImage(t.paymentScreenshot)}>
                        <img src={t.paymentScreenshot} alt="Payment Receipt Thumbnail" />
                        <span className="click-view">
                          <Eye size={12} /> View Receipt
                        </span>
                      </ReceiptPreview>
                    ) : (
                      <span className="dim">No screenshot attached</span>
                    )}
                  </div>

                  {t.declineReason && (
                    <div className="decline-box">
                      <strong>Decline Reason:</strong> {t.declineReason}
                    </div>
                  )}

                  <div className="timestamp-row">
                    <span>
                      Submitted:{" "}
                      {t.paymentSubmittedAt
                        ? new Date(t.paymentSubmittedAt).toLocaleString("en-IN")
                        : new Date(t.timestamp).toLocaleString("en-IN")}
                    </span>
                  </div>
                </ProofSection>

                {/* Actions: Confirm or Decline Buttons */}
                <ActionsRow>
                  {isPending && (
                    <>
                      <ApproveBtn
                        onClick={() => handleConfirmTrade(t)}
                        disabled={actionLoading}
                      >
                        <CheckCircle size={15} /> Confirm Trade
                      </ApproveBtn>
                      <DeclineBtn
                        onClick={() => {
                          setDeclineModalTrade(t);
                          setDeclineReason("");
                        }}
                        disabled={actionLoading}
                      >
                        <XCircle size={15} /> Decline
                      </DeclineBtn>
                    </>
                  )}

                  {isConfirmed && (
                    <div className="confirmed-note">
                      <CheckCircle size={14} color="#10b981" />
                      <span>Verified & Added to Customer Portfolio</span>
                    </div>
                  )}

                  {isDeclined && (
                    <ReopenBtn
                      onClick={() => handleConfirmTrade(t)}
                      disabled={actionLoading}
                    >
                      Re-verify & Approve
                    </ReopenBtn>
                  )}
                </ActionsRow>
              </PaymentCard>
            );
          })}
        </PaymentsGrid>
      )}

      {/* 🖼️ Fullscreen Screenshot Modal */}
      {previewImage && (
        <ModalOverlay onClick={() => setPreviewImage(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <button className="close-x" onClick={() => setPreviewImage(null)}>
              ✕
            </button>
            <img src={previewImage} alt="Payment Screenshot Fullscreen" />
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ❌ Decline Reason Modal */}
      {declineModalTrade && (
        <ModalOverlay onClick={() => setDeclineModalTrade(null)}>
          <DeclineModalForm onClick={(e) => e.stopPropagation()} onSubmit={handleDeclineSubmit}>
            <h3>Decline Trade Payment</h3>
            <p>
              Please enter the reason for declining order{" "}
              <strong>#{formatTradeId(declineModalTrade)}</strong>:
            </p>

            <textarea
              rows={3}
              placeholder="e.g. UTR not found in HDFC Bank account, invalid amount, fake screenshot..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              required
            />

            <div className="quick-reasons">
              <span onClick={() => setDeclineReason("Payment not credited in HDFC bank account.")}>
                Payment not credited
              </span>
              <span onClick={() => setDeclineReason("UTR / Transaction reference mismatch.")}>
                UTR Mismatch
              </span>
              <span onClick={() => setDeclineReason("Incorrect amount transferred.")}>
                Amount Mismatch
              </span>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setDeclineModalTrade(null)}
              >
                Cancel
              </button>
              <button type="submit" className="confirm-decline-btn" disabled={actionLoading}>
                {actionLoading ? "Declining..." : "Confirm Decline"}
              </button>
            </div>
          </DeclineModalForm>
        </ModalOverlay>
      )}
    </Wrapper>
  );
};

export default AdminPaymentsTab;

/* ======================================================
   💅 STYLED COMPONENTS (RESPONSIVE ADMIN INTERFACE)
   ====================================================== */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 12px;
  padding: 1rem 1.2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  .icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(245, 215, 110, 0.12);
    color: #f5d76e;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .info {
    display: flex;
    flex-direction: column;

    .count {
      font-size: 1.4rem;
      font-weight: 800;
      color: #fff;
      line-height: 1.1;
    }

    .label {
      font-size: 0.78rem;
      color: #94a3b8;
      margin-top: 3px;
    }
  }

  &.alert {
    border-color: rgba(245, 215, 110, 0.4);
    .icon-wrap {
      background: rgba(245, 215, 110, 0.2);
      color: #f5d76e;
    }
    .count {
      color: #f5d76e;
    }
  }

  &.success {
    border-color: rgba(16, 185, 129, 0.4);
    .icon-wrap {
      background: rgba(16, 185, 129, 0.15);
      color: #10b981;
    }
    .count {
      color: #10b981;
    }
  }

  &.danger {
    border-color: rgba(239, 68, 68, 0.35);
    .icon-wrap {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
    }
    .count {
      color: #ef4444;
    }
  }
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 12px;
  padding: 0.85rem 1rem;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const FilterTabBtn = styled.button`
  background: ${({ $active }) => ($active ? "#f5d76e" : "#1a2234")};
  color: ${({ $active }) => ($active ? "#000" : "#94a3b8")};
  border: 1px solid ${({ $active }) => ($active ? "#f5d76e" : "#243048")};
  padding: 0.4rem 0.85rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ $active }) => ($active ? "#000" : "#fff")};
  }

  &.pending-tab {
    position: relative;
  }
`;

const SearchAndRefresh = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-left: auto;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #0b0f17;
  border: 1px solid #1f293d;
  border-radius: 8px;
  padding: 0.45rem 0.75rem;
  color: #94a3b8;

  input {
    background: transparent;
    border: none;
    outline: none;
    color: #fff;
    font-size: 0.82rem;
    width: 170px;
  }
`;

const RefreshBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #1f293d;
  color: #cbd5e1;
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 0.45rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #2a374f;
    color: #fff;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PaymentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.25rem;
`;

const PaymentCard = styled.div`
  background: #111827;
  border: 1.5px solid
    ${({ $status }) =>
      $status === "pending_verification"
        ? "rgba(245, 215, 110, 0.45)"
        : $status === "confirmed"
        ? "rgba(16, 185, 129, 0.35)"
        : $status === "declined"
        ? "rgba(239, 68, 68, 0.35)"
        : "#1f293d"};
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const UserInfo = styled.div`
  h4 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #fff;
  }

  .phone {
    font-size: 0.82rem;
    color: #94a3b8;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;

    .wa-btn {
      color: #10b981;
      background: rgba(16, 185, 129, 0.15);
      border-radius: 4px;
      padding: 2px 4px;
      display: inline-flex;
      align-items: center;
    }
  }
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  letter-spacing: 0.5px;

  background: ${({ $status }) =>
    $status === "pending_verification"
      ? "rgba(245, 215, 110, 0.15)"
      : $status === "confirmed"
      ? "rgba(16, 185, 129, 0.15)"
      : $status === "declined"
      ? "rgba(239, 68, 68, 0.15)"
      : "rgba(148, 163, 184, 0.15)"};

  color: ${({ $status }) =>
    $status === "pending_verification"
      ? "#f5d76e"
      : $status === "confirmed"
      ? "#10b981"
      : $status === "declined"
      ? "#ef4444"
      : "#94a3b8"};

  border: 1px solid
    ${({ $status }) =>
      $status === "pending_verification"
        ? "rgba(245, 215, 110, 0.35)"
        : $status === "confirmed"
        ? "rgba(16, 185, 129, 0.35)"
        : $status === "declined"
        ? "rgba(239, 68, 68, 0.35)"
        : "rgba(148, 163, 184, 0.35)"};
`;

const TradeSummary = styled.div`
  background: #0b0f17;
  border: 1px solid #1f293d;
  border-radius: 10px;
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .amount-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #1f293d;
    padding-bottom: 0.4rem;

    .title {
      font-size: 0.8rem;
      color: #94a3b8;
    }

    .amount {
      font-size: 1.3rem;
      font-weight: 800;
      color: #f5d76e;
    }
  }

  .meta-info {
    display: flex;
    justify-content: space-between;
    font-size: 0.8rem;
    color: #cbd5e1;

    .action-badge {
      font-weight: 700;
      padding: 1px 4px;
      border-radius: 4px;

      &.buy {
        color: #48ff91;
        background: rgba(72, 255, 145, 0.1);
      }

      &.sell {
        color: #f87171;
        background: rgba(248, 113, 113, 0.1);
      }
    }
  }
`;

const ProofSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-size: 0.82rem;

  .utr-row,
  .screenshot-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .lbl {
      color: #94a3b8;
      width: 95px;
      flex-shrink: 0;
    }

    .val {
      color: #f1f5f9;
      font-weight: 600;

      &.mono {
        font-family: monospace;
        color: #f5d76e;
      }
    }

    .dim {
      color: #64748b;
      font-style: italic;
    }
  }

  .copy-btn {
    background: #1f293d;
    color: #e2e8f0;
    border: none;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 0.72rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-left: auto;
  }

  .decline-box {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.25);
    padding: 0.45rem 0.65rem;
    border-radius: 6px;
    color: #fca5a5;
    font-size: 0.78rem;
  }

  .timestamp-row {
    color: #64748b;
    font-size: 0.72rem;
    margin-top: 4px;
  }
`;

const ReceiptPreview = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  background: #1a2234;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #2e3d5b;

  img {
    width: 30px;
    height: 30px;
    border-radius: 4px;
    object-fit: cover;
  }

  .click-view {
    font-size: 0.75rem;
    color: #60a5fa;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.65rem;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid #1f293d;

  .confirmed-note {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #10b981;
    font-size: 0.8rem;
    font-weight: 600;
  }
`;

const ApproveBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: #10b981;
  color: #000;
  font-weight: 700;
  font-size: 0.84rem;
  padding: 0.55rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #059669;
    color: #fff;
  }
`;

const DeclineBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.4);
  font-weight: 700;
  font-size: 0.84rem;
  padding: 0.55rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #ef4444;
    color: #fff;
  }
`;

const ReopenBtn = styled.button`
  width: 100%;
  background: #334155;
  color: #f1f5f9;
  border: none;
  padding: 0.45rem;
  border-radius: 6px;
  font-size: 0.78rem;
  cursor: pointer;

  &:hover {
    background: #475569;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalBox = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: #0f141c;
  border-radius: 12px;
  padding: 0.5rem;
  border: 1.5px solid #f5d76e;

  img {
    max-width: 100%;
    max-height: 85vh;
    border-radius: 8px;
    display: block;
  }

  .close-x {
    position: absolute;
    top: -12px;
    right: -12px;
    background: #ef4444;
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    font-size: 1.1rem;
    font-weight: bold;
    cursor: pointer;
  }
`;

const DeclineModalForm = styled.form`
  background: #111827;
  border: 1.5px solid #ef4444;
  border-radius: 14px;
  padding: 1.5rem;
  width: 100%;
  max-width: 460px;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  h3 {
    margin: 0;
    color: #ef4444;
    font-size: 1.15rem;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: #94a3b8;
  }

  textarea {
    background: #0b0f17;
    border: 1px solid #1f293d;
    border-radius: 8px;
    padding: 0.75rem;
    color: #fff;
    font-size: 0.88rem;
    resize: none;

    &:focus {
      outline: none;
      border-color: #ef4444;
    }
  }

  .quick-reasons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;

    span {
      font-size: 0.72rem;
      background: #1f293d;
      color: #94a3b8;
      padding: 0.25rem 0.55rem;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        background: #334155;
        color: #fff;
      }
    }
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 0.5rem;

    button {
      padding: 0.5rem 1rem;
      border-radius: 6px;
      font-weight: 700;
      font-size: 0.84rem;
      cursor: pointer;
      border: none;
    }

    .cancel-btn {
      background: #334155;
      color: #cbd5e1;
    }

    .confirm-decline-btn {
      background: #ef4444;
      color: #fff;
    }
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  gap: 1rem;
  color: #94a3b8;
  .spinner {
    animation: spin 1s linear infinite;
  }
`;

const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  text-align: center;
  color: #94a3b8;

  h3 {
    margin: 1rem 0 0.25rem 0;
    color: #fff;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    max-width: 420px;
  }
`;
